import React, { useEffect, useRef } from 'react';
import { Route, Switch } from 'react-router-dom';
import { FingotiNotFound, FingotiLoading } from '@fingoti/components';
import { RequireAuth, useFingotiAuth, useOrgContextSync } from '@fingoti/auth-js/react';

import Layout from './components/Layout';
import { Dashboard } from './components/dashboard/Dashboard';
import { DeviceTabs } from './components/device/DeviceTabs';
import { GatewayTabs } from './components/device/gateway/GatewayTabs';
import { NodeTabs } from './components/device/node/NodeTabs';
import { Webhooks } from './components/engine/webhooks/Webhooks';
import { Settings } from './components/settings/Settings';
import { ReportTabs } from './components/report/ReportTabs';
import { Callback } from './components/Auth/Callback';
import { Logout } from './components/Login/Logout';

import { ProfileData } from './context/ProfileData';
import { deviceHub } from './services/signalr.service';
import { orgContextChannel } from './services/authConfig';
import 'react-json-pretty/themes/monikai.css';
import './main.css';

const Protected = ({ children }) => (
	<RequireAuth fallback={<FingotiLoading fullHeight />}>{children}</RequireAuth>
);

const App = () => {
	const profileData = ProfileData();
	const { isAuthenticated, session } = useFingotiAuth();
	const activeOid = session?.oid ?? null;
	const loadedOidRef = useRef(undefined);

	// Mirror the active org-context chain to/from the shared cross-subdomain
	// cookie so a switch / act-as in another Fingoti app follows the user here
	// (and ours follows them). Only org ids travel in the cookie — never tokens.
	useOrgContextSync(orgContextChannel);

	// Single source of truth for loading app data: (re)load the profile whenever
	// the active org changes. This covers the initial login, an in-portal switch
	// (Layout just calls enter/exitOrgContext), and an externally-driven change
	// reconciled from the shared cookie. On a change (not the first load) the
	// SignalR hub bound to the previous org is torn down first so it reconnects
	// against the new context's token.
	useEffect(() => {
		if (!isAuthenticated) {
			loadedOidRef.current = undefined;
			return;
		}
		if (activeOid === loadedOidRef.current) return;

		const isFirstLoad = loadedOidRef.current === undefined;
		loadedOidRef.current = activeOid;

		const load = async () => {
			if (!isFirstLoad) {
				// Suppresses the "live updates disconnected" snackbar in DeviceData's
				// onclose handler; profileData.getData() rebuilds the hub for the new org.
				localStorage.setItem('switchingOrganisation', true);
				try {
					if (deviceHub && deviceHub.state === 'Connected') {
						await deviceHub.invoke('RemoveFromOrganisation');
					}
					if (deviceHub) {
						await deviceHub.stop();
					}
				} catch (error) {
					console.warn('SignalR teardown on org-context change failed', error);
				}
			}
			// getData surfaces its own failures (snackbar + ERROR dispatch) and a
			// 401 triggers a reauth redirect, so swallow the rejection here to avoid
			// an unhandled promise rejection.
			profileData.getData().catch(() => {});
		};

		load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAuthenticated, activeOid]);

	return (
		<div className='App'>
			<Layout>
				<Switch>
					<Route exact path='/auth/callback' component={Callback} />
					<Route exact path='/logout' component={Logout} />

					<Route exact path='/'>
						<Protected><Dashboard /></Protected>
					</Route>
					<Route exact path='/devices'>
						<Protected><DeviceTabs activeMenu={0} /></Protected>
					</Route>
					<Route exact path='/devices/gateways'>
						<Protected><DeviceTabs activeMenu={1} /></Protected>
					</Route>
					<Route exact path='/devices/gateways/:id'>
						<Protected><GatewayTabs /></Protected>
					</Route>
					<Route exact path='/devices/nodes'>
						<Protected><DeviceTabs activeMenu={2} /></Protected>
					</Route>
					<Route exact path='/devices/nodes/:id'>
						<Protected><NodeTabs /></Protected>
					</Route>

					<Route exact path='/webhooks'>
						<Protected><Webhooks activeMenu={0} /></Protected>
					</Route>
					<Route exact path='/webhooks/logs'>
						<Protected><Webhooks activeMenu={1} /></Protected>
					</Route>
					<Route exact path='/webhooks/:whid'>
						<Protected><Webhooks activeMenu={0} /></Protected>
					</Route>

					<Route exact path='/settings'>
						<Protected><Settings activeMenu={0} /></Protected>
					</Route>
					<Route exact path='/settings/presets'>
						<Protected><Settings activeMenu={1} /></Protected>
					</Route>
					<Route exact path='/settings/following'>
						<Protected><Settings activeMenu={2} /></Protected>
					</Route>

					<Route exact path='/reports'>
						<Protected><ReportTabs activeMenu={0} /></Protected>
					</Route>
					<Route exact path='/reports/command-log'>
						<Protected><ReportTabs activeMenu={1} /></Protected>
					</Route>

					{/* Keep at bottom */}
					<Route component={FingotiNotFound} />
				</Switch>
			</Layout>
		</div>
	);
};

export default App;
